// Webhooks: outbound, HMAC-signed event deliveries to your HTTPS endpoint (rbac-restore R2,
// task 2.2). List is open to members (GET /webhooks); create, delete and "Send test" need
// `configure_webhooks`, and create is plan-gated by `webhooks.config`.
// Shapes follow the handler (gateway/api/webhooks.py), see WebhookConfig in api/types.ts.
// Note: the gateway's dispatch_webhook() has no caller yet, so today only test pings are
// delivered; the copy here does not promise live event traffic.
import { useId, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { errorMessage } from '../api/errors'
import { remedyFor, useEntitlements, useFeature } from '../entitlements'
import { UpsellLink } from '../entitlements/PlanNote'
import { qk } from '../api/queries'
import type { WebhookConfig, WebhookCreate, WebhookTestResult } from '../api/types'
import { useSession } from '../context/SessionContext'
import { useCan } from '../rbac'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { formatDate } from '../components/format'
import { Badge, EmptyState, PageHeader, QueryState } from '../components/ui'

/** Client-side mirror of WebhookCreateRequest.must_be_https (the gateway re-checks: 422). */
function httpsUrlProblem(raw: string): string | null {
  const v = raw.trim()
  if (!v) return 'Enter the endpoint URL.'
  if (!v.toLowerCase().startsWith('https://')) return 'The endpoint must use HTTPS (https://…).'
  try {
    const u = new URL(v)
    if (!u.hostname) return 'Enter a valid URL.'
  } catch {
    return 'Enter a valid URL.'
  }
  return null
}

function randomSecret(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

function parseEvents(raw: string): string[] {
  const list = raw.split(',').map((s) => s.trim()).filter(Boolean)
  return list.length ? [...new Set(list)] : ['*']
}

function CreateWebhookModal({ onClose }: { onClose: () => void }) {
  const id = useId()
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const [url, setUrl] = useState('')
  const [secret, setSecret] = useState('')
  const [events, setEvents] = useState('*')
  const [touched, setTouched] = useState(false)

  const urlProblem = httpsUrlProblem(url)
  const secretProblem = secret.trim() ? null : 'Enter a signing secret (or generate one).'

  const create = useMutation({
    mutationFn: (body: WebhookCreate) =>
      apiFetch<WebhookConfig>('/webhooks', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.webhooks(workspaceId) })
      onClose()
    },
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setTouched(true)
    if (urlProblem || secretProblem || create.isPending) return
    create.mutate({ endpoint_url: url.trim(), secret, events: parseEvents(events) })
  }

  return (
    <Modal title="Add webhook" onClose={onClose}>
      <form onSubmit={submit} aria-label="Add webhook form" noValidate>
        <div className="field">
          <label htmlFor={`${id}-url`}>Endpoint URL</label>
          <input
            id={`${id}-url`}
            type="url"
            inputMode="url"
            value={url}
            placeholder="https://example.com/hooks/symone"
            aria-invalid={touched && !!urlProblem}
            onChange={(e) => setUrl(e.target.value)}
          />
          {touched && urlProblem && <span className="form-error" role="alert">{urlProblem}</span>}
        </div>
        <div className="field">
          <label htmlFor={`${id}-secret`}>Signing secret</label>
          <div className="input-with-action">
            <input
              id={`${id}-secret`}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={secret}
              aria-invalid={touched && !!secretProblem}
              onChange={(e) => setSecret(e.target.value)}
            />
            <button type="button" className="btn btn--sm" onClick={() => setSecret(randomSecret())}>Generate</button>
          </div>
          <span className="field-hint">
            Each delivery is signed with <code>X-Symone-Signature: sha256=…</code> (HMAC-SHA256 of the body). Copy the secret
            now: Symone stores it encrypted and never shows it again.
          </span>
          {touched && secretProblem && <span className="form-error" role="alert">{secretProblem}</span>}
        </div>
        <div className="field">
          <label htmlFor={`${id}-events`}>Events</label>
          <input id={`${id}-events`} value={events} onChange={(e) => setEvents(e.target.value)} />
          <span className="field-hint">Comma-separated event types, or <code>*</code> for every event.</span>
        </div>
        {create.error ? <p className="form-error" role="alert">{errorMessage(create.error)}</p> : null}
        <div className="modal-foot modal-foot--inline">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={create.isPending} aria-busy={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add webhook'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function DeleteWebhookModal({ hook, onClose }: { hook: WebhookConfig; onClose: () => void }) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const del = useMutation({
    mutationFn: () => apiFetch(`/webhooks/${encodeURIComponent(hook.id)}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.webhooks(workspaceId) })
      onClose()
    },
  })
  return (
    <Modal
      title="Delete webhook"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn--danger" disabled={del.isPending} aria-busy={del.isPending} onClick={() => del.mutate()}>
            {del.isPending ? 'Deleting…' : 'Delete webhook'}
          </button>
        </>
      }
    >
      <p>
        Stop sending events to <code>{hook.endpoint_url}</code>? This can’t be undone.
      </p>
      {del.error ? <p className="form-error" role="alert">{errorMessage(del.error)}</p> : null}
    </Modal>
  )
}

function TestButton({ hook }: { hook: WebhookConfig }) {
  const test = useMutation({
    mutationFn: () => apiFetch<WebhookTestResult>(`/webhooks/${encodeURIComponent(hook.id)}/test`, { method: 'POST' }),
  })
  const r = test.data
  return (
    <>
      <button
        type="button"
        className="btn btn--sm"
        disabled={test.isPending}
        aria-busy={test.isPending}
        aria-label={`Send test to ${hook.endpoint_url}`}
        onClick={() => test.mutate()}
      >
        {test.isPending ? 'Sending…' : 'Send test'}
      </button>
      <span role="status" className="cell-sub">
        {r && r.status === 'sent' && `Delivered (HTTP ${r.response_code ?? '?'})`}
        {r && r.status !== 'sent' && `Failed: ${r.error ?? `HTTP ${r.response_code ?? '?'}`}`}
        {test.error ? errorMessage(test.error) : null}
      </span>
    </>
  )
}

export function WebhooksView() {
  const { workspaceId } = useSession()
  const canConfigure = useCan('configure_webhooks')
  const plan = useFeature('webhooks.config')
  const ent = useEntitlements().data
  const planKnown = !!ent
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<WebhookConfig | null>(null)
  const q = useQuery({
    queryKey: qk.webhooks(workspaceId),
    queryFn: () => apiFetch<WebhookConfig[]>('/webhooks'),
  })
  const hooks = q.data ?? []
  // Disabled while the plan is loading or unreadable (fail closed), and when not in the plan.
  const planOff = canConfigure && !plan.allowed
  const showNote = planOff && !plan.loading

  return (
    <div className="page">
      <PageHeader
        title="Webhooks"
        count={q.data ? hooks.length : undefined}
        description="HTTPS endpoints that receive HMAC-signed event deliveries from this workspace. Send a test ping to check an endpoint verifies the signature."
        actions={
          canConfigure && (
            <button
              type="button"
              className="btn btn--primary"
              disabled={planOff}
              aria-describedby={showNote ? 'webhooks-plan' : undefined}
              onClick={() => setCreating(true)}
            >
              <Icon name="plus" size={14} /> Add webhook
            </button>
          )
        }
      />
      {showNote && (
        <p className="warn-note" id="webhooks-plan" role="note">
          {planKnown ? (
            <>
              Webhooks aren’t included in your plan. {remedyFor(ent, 'webhooks.config')} <UpsellLink />
            </>
          ) : (
            plan.reason
          )}
        </p>
      )}
      <QueryState isLoading={q.isLoading} error={q.error} what="webhooks" onRetry={() => q.refetch()}>
        {hooks.length === 0 ? (
          <EmptyState
            icon="link"
            title="No webhooks yet"
            body={
              canConfigure
                ? 'Add an HTTPS endpoint to receive signed event deliveries from this workspace.'
                : 'Webhooks send signed event deliveries to an HTTPS endpoint. Ask a workspace owner or admin to add one.'
            }
            sampleHeaders={['Endpoint', 'Events', 'Status', 'Created']}
            sampleRows={[['https://example.com/hooks', '*', <Badge tone="success">active</Badge>, 'Oct 2, 2026']]}
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Endpoint</th>
                  <th>Events</th>
                  <th>Status</th>
                  <th>Created</th>
                  {canConfigure && <th><span className="sr-only">Actions</span></th>}
                </tr>
              </thead>
              <tbody>
                {hooks.map((h) => (
                  <tr key={h.id}>
                    <td><code>{h.endpoint_url}</code></td>
                    <td>{h.events.join(', ')}</td>
                    <td><Badge tone={h.is_active ? 'success' : 'neutral'}>{h.is_active ? 'active' : 'inactive'}</Badge></td>
                    <td>{formatDate(h.created_at)}</td>
                    {canConfigure && (
                      <td className="cell-actions">
                        <div className="form-actions">
                          <TestButton hook={h} />
                          <button
                            type="button"
                            className="btn btn--sm btn--ghost-danger"
                            aria-label={`Delete ${h.endpoint_url}`}
                            onClick={() => setDeleting(h)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>

      {creating && <CreateWebhookModal onClose={() => setCreating(false)} />}
      {deleting && <DeleteWebhookModal hook={deleting} onClose={() => setDeleting(null)} />}
    </div>
  )
}
